import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {
    AnyImageOpHalData,
    CtorArgs,
    ImageOpHalData,
    ImageOpHalDataParameters,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {applyAddressModeToRegion} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-utils"
import {RegionLockedHalImageRef} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/region-locked-hal-image-ref"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {Descriptor, MipLevelRegion, Region, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/affine-transform/definition"
import {computeResultSize} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/affine-transform/helpers"
import {HalImage} from "@cm/hal"
import {HalPainterImageCompositorRef} from "@cm/hal/hal-ref"
import {Box2, Box2Like, Matrix3x2, Vector2, Vector2Like} from "@cm/math"
import {getHalAddressMode} from "@cm/webgl2"
import {UPDATE_COMPLEXITY_MID} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

const MIN_MIP_LEVEL_BIAS = 0 // don't allow negative mip level bias as it can blow up memory usage
const MAX_MIP_LEVEL_BIAS = Number.POSITIVE_INFINITY

type Definition = typeof definition

export class ImageOpAffineTransformHal extends ImageOpHal<Definition, ImageOpAffineTransformHalData> {
    constructor(ctx: ImageOpContextHal, parameters: ImageOpHalParameters<Definition>) {
        super(ctx)
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpAffineTransformHalData>): ImageOpAffineTransformHalData {
        return new ImageOpAffineTransformHalData({
            ...args,
            descriptor: this.determineDescriptor(args.parameters),
            outputImageReuse: "reuse-if-same-descriptor",
            updateComplexity: UPDATE_COMPLEXITY_MID,
        })
    }

    private determineDescriptor(parameters: ImageOpHalDataParameters<Definition>): Descriptor {
        const resultBox = parameters.resultSize
            ? Box2.fromSize(parameters.resultSize)
            : computeResultSize(parameters.sourceImage.descriptor, parameters.transform)
        return {
            width: resultBox.width,
            height: resultBox.height,
            channels: parameters.sourceImage.descriptor.channels,
        }
    }
}

class ImageOpAffineTransformHalData extends ImageOpHalData<Definition> {
    constructor(args: CtorArgs<Definition, ImageOpAffineTransformHalData>) {
        super(args)
        this.compositor = args.prevData?.compositor.addRef() ?? this.createCompositor()
        this.resultOffset = this.determineResultOffset()
    }

    protected override dispose() {
        this.compositor.release()
        super.dispose()
    }

    protected override async writeRegion(outputImage: HalImage, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void> {
        const {sourceImage, transform, addressMode} = this.parameters
        sourceImage.addRef("AffineTransform.writeRegion - sourceImage")
        let sourceHalImage: RegionLockedHalImageRef | undefined
        try {
            const invTransform = Matrix3x2.fromMatrix3x2Like(transform).inverse()

            // update source image
            const sourceImageMipLevelBias = this.computeSourceImageMipLevelBias(sourceImage, invTransform)
            const sourceImageMipLevel = Math.max(0, Math.min(sourceImage.numMipLevels - 1, mipLevelRegion.mipLevel + sourceImageMipLevelBias))
            const sourceRegion = this.determineSourceRegion(
                sourceImage,
                mipLevelRegion,
                mipLevelRegion.mipLevel,
                this.resultOffset,
                sourceImageMipLevel,
                invTransform,
            )
            sourceHalImage = await sourceImage.getUpdatedHalImage(sourceRegion, options)

            this.compositor.ref.paint({
                target: outputImage,
                sourceImages: sourceHalImage.halImage,
                parameters: {
                    u_invTransform: {type: "float3x2", value: invTransform},
                    u_sourceMipLevel: {type: "int", value: sourceImageMipLevel},
                    u_addressMode: {type: "int", value: getHalAddressMode(addressMode)},
                    u_resultOffset: {type: "float2", value: this.resultOffset},
                },
                options: {
                    targetRegion: mipLevelRegion,
                    targetMipLevel: mipLevelRegion.mipLevel,
                },
            })
        } finally {
            sourceHalImage?.release()
            sourceImage.release("AffineTransform.writeRegion - sourceImage")
        }
    }

    protected override onSourceRegionsInvalidated(_sourceImage: AnyImageOpHalData, sourceRegions: Regions) {
        const targetRegions = sourceRegions.flatMap((sourceRegion) => {
            const transform = Matrix3x2.fromMatrix3x2Like(this.parameters.transform)
            const targetRegion00 = transform.multiplyVector({
                x: sourceRegion.x,
                y: sourceRegion.y,
            })
            const targetRegion11 = transform.multiplyVector({
                x: sourceRegion.x + sourceRegion.width,
                y: sourceRegion.y + sourceRegion.height,
            })
            const p00 = Vector2.min(targetRegion00, targetRegion11).floorInPlace()
            const p11 = Vector2.max(targetRegion00, targetRegion11).ceilInPlace()
            const targetRegion: Region = {
                x: p00.x,
                y: p00.y,
                width: p11.x - p00.x,
                height: p11.y - p00.y,
            }
            return applyAddressModeToRegion(this.descriptor, targetRegion, 0, this.parameters.addressMode)
        })
        this.invalidateRegions(targetRegions)
    }

    private computeSourceImageMipLevelBias(sourceImage: AnyImageOpHalData, invTransform: Matrix3x2) {
        const a = invTransform.elements[0]
        const b = invTransform.elements[1]
        const c = invTransform.elements[2]
        const d = invTransform.elements[3]
        const sqrABCD = a * a + b * b + c * c + d * d
        const det = a * d - b * c
        const maxScalingFactor = Math.sqrt((sqrABCD + Math.sqrt(Math.max(0, sqrABCD * sqrABCD - 4 * det * det))) / 2) + 1e-6 // add slight margin to switch mip levels due to precision issues
        let mipLevelBias = Math.floor(Math.log2(maxScalingFactor))
        mipLevelBias = Math.max(MIN_MIP_LEVEL_BIAS, Math.min(MAX_MIP_LEVEL_BIAS, mipLevelBias))
        return mipLevelBias
    }

    private determineSourceRegion(
        sourceImage: AnyImageOpHalData,
        region: Region,
        mipLevel: number,
        resultOffset: Vector2Like,
        sourceImageMipLevel: number,
        invTransform: Matrix3x2,
    ): MipLevelRegion {
        // scale region to mip level 0
        const mipLevelScale = 1 << mipLevel
        const scaledRegion: Box2Like = {
            x: region.x * mipLevelScale + resultOffset.x,
            y: region.y * mipLevelScale + resultOffset.y,
            width: region.width * mipLevelScale,
            height: region.height * mipLevelScale,
        }
        // project the region corners to the source image
        const p00 = new Vector2(scaledRegion.x, scaledRegion.y)
        const p10 = new Vector2(scaledRegion.x + scaledRegion.width, scaledRegion.y)
        const p11 = new Vector2(scaledRegion.x + scaledRegion.width, scaledRegion.y + scaledRegion.height)
        const p01 = new Vector2(scaledRegion.x, scaledRegion.y + scaledRegion.height)
        const s00 = invTransform.multiplyVector(p00)
        const s10 = invTransform.multiplyVector(p10)
        const s11 = invTransform.multiplyVector(p11)
        const s01 = invTransform.multiplyVector(p01)
        const margin = 1 // add margin for interpolation
        const minX = Math.floor(Math.min(s00.x, s10.x, s11.x, s01.x) / mipLevelScale - margin)
        const maxX = Math.ceil(Math.max(s00.x, s10.x, s11.x, s01.x) / mipLevelScale + margin)
        const minY = Math.floor(Math.min(s00.y, s10.y, s11.y, s01.y) / mipLevelScale - margin)
        const maxY = Math.ceil(Math.max(s00.y, s10.y, s11.y, s01.y) / mipLevelScale + margin)
        return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            mipLevel: sourceImageMipLevel,
        }
    }

    private createCompositor() {
        return this.ctx.createPainter(
            "compositor",
            `
                uniform mat3x2 u_invTransform;
                uniform int u_sourceMipLevel;
                uniform int u_addressMode;
                uniform vec2 u_resultOffset;

                vec4 computeColor(ivec2 targetPixel) {
                    float scaling = float(1 << u_sourceMipLevel);
                    vec2 sourcePixel = u_invTransform * vec3((vec2(targetPixel) + 0.5) * scaling + u_resultOffset, 1) / scaling; // we offset by 0.5 because texturePx samples at the center of the texel
                    
                    // return vec4(vec2(sourcePixel) / 512.0, 0.0, 1.0); // placeholder color
                    
                    return textureLodPx0(sourcePixel, u_sourceMipLevel, u_addressMode);
                }
            `,
        )
    }

    private determineResultOffset(): Vector2Like {
        const resultBox = this.parameters.resultSize
            ? Box2.fromSize(this.parameters.resultSize)
            : computeResultSize(this.parameters.sourceImage.descriptor, this.parameters.transform)
        return this.parameters.options.compensateOffset
            ? {
                  x: resultBox.x,
                  y: resultBox.y,
              }
            : {
                  x: 0,
                  y: 0,
              }
    }

    private readonly compositor: HalPainterImageCompositorRef
    private resultOffset: Vector2Like = {x: 0, y: 0}
}
